<nz-spin [nzSpinning]="loading$ | async">
  <form nz-form class="form" [formGroup]="templateForm">
    <div class="drawer-form-wrapper">
      <div class="legend">
        <svg-icon
          src="/assets/icon/icon-info-circle-sm.svg"
          class="icon s-16 legend-icon"
          aria-label="Info"></svg-icon>
        <div>
          These settings control the information displayed in the
          <b>printed BIP report</b>. Changes apply to all future reports
          generated from this template.
        </div>
      </div>
      <div class="template-card">
        <div class="card-header mb-24">
          <div class="card-header-left">
            <img src="/assets/images/logo-lg.png" alt="logo" />
          </div>
          <div class="card-header-right">
            <h3 class="header-title">&lt;School Name&gt;</h3>
            <p class="header-desc">&lt;School Address&gt;</p>
          </div>
        </div>
        <div class="card-header mb-24">
          <div class="card-header-left">
            <h3 class="title s-18">&lt;Student Name&gt;</h3>
          </div>
          <div class="card-header-right">
            <h3 class="title s-18">
              Supervisor:
              <span class="header-sec-desc">&lt;Supervisor Name&gt;</span>
            </h3>
          </div>
        </div>

        <nz-form-item>
          <span nz-checkbox formControlName="isDefault" class="custom-checkbox"
            >Set as Default</span
          >
        </nz-form-item>

        <div class="form-grid">
          <nz-form-item>
            <nz-form-label nzRequired nzFor="templateName"
              >Template Name</nz-form-label
            >
            <nz-form-control [nzErrorTip]="nameErrorTpl">
              <input
                nz-input
                id="templateName"
                aria-label="Template Name"
                formControlName="templateName"
                placeholder="Enter template name" />
              <ng-template #nameErrorTpl let-control>
                <ng-container *ngIf="control.hasError('required')"
                  >Please enter a template name</ng-container
                >
                <ng-container
                  *ngIf="
                    control.hasError('maxlength') ||
                    control.hasError('minlength')
                  ">
                  Template name must be between 3 and 250 characters
                </ng-container>
              </ng-template>
            </nz-form-control>
          </nz-form-item>
        </div>

        <div class="checkbox-grid mb-16" formArrayName="templateFields">
          <div
            class="wrapper"
            *ngFor="
              let ctrl of templateFieldControls;
              let i = index;
              trackBy: trackByIndex
            "
            [formGroup]="ctrl">
            <span
              nz-checkbox
              [nzChecked]="ctrl.value.isSelected"
              [nzDisabled]="isViewMode"
              (nzCheckedChange)="toggleTemplateField(i, $event)"
              >{{ ctrl.value.fieldName }}</span
            >
          </div>
        </div>

        <nz-form-item>
          <span
            nz-checkbox
            formControlName="isIncludeStrategyGuidelines"
            class="custom-checkbox"
            >Include Strategy Guidelines</span
          >
        </nz-form-item>
        <div
          class="error-message"
          *ngIf="!isViewMode && !isTemplateFieldsValid">
          Select at least one template field
        </div>
      </div>

      <div class="legend">
        <svg-icon
          src="/assets/icon/icon-info-circle-sm.svg"
          class="icon s-16 legend-icon"
          aria-label="Info"></svg-icon>
        <div>
          <p class="legend-line">
            These settings control workflow behavior, data entry, and user
            interactions in the BIP process. Changes apply to all future plans
            using this template.
          </p>
          <p class="legend-line">
            <b>Required:</b> Must be completed before the plan can be finalized.
          </p>
          <p class="legend-line">
            <b>Hard Stop:</b> Prevents the user from proceeding until completed.
            Automatically sets Required.
          </p>
          <p class="legend-line">
            <b>Help Text:</b> Use the
            <svg-icon
              src="/assets/icon/icon-help.svg"
              class="icon s-14 inline-icon"
              aria-label="Help"></svg-icon>
            icon to add guidance for end users.
          </p>
        </div>
      </div>

      <div formArrayName="sections">
        <div
          class="section-block"
          [class.off]="!isSectionSelected(sectionGroup)"
          *ngFor="
            let sectionGroup of sections.controls;
            let i = index;
            trackBy: trackBySectionName
          "
          [formGroupName]="i">
          <div class="section-row">
            <div class="left">
              <span class="badge">Section {{ i + 1 }}</span>
              <h4 class="section-title">
                {{ getSectionDisplayName(sectionGroup.value.sectionName) }}
              </h4>
              <span
                class="section-error"
                *ngIf="getSectionErrorMessage(sectionGroup) as sectionError"
                nz-tooltip
                [nzTooltipTitle]="sectionError"
                >{{ sectionError }}</span
              >
            </div>
            <div class="row-controls">
              <span
                nz-checkbox
                class="cb-small"
                formControlName="isRequired"
                (nzCheckedChange)="
                  onSectionRequiredChange(sectionGroup, $event)
                "
                >Required</span
              >
              <span
                nz-checkbox
                class="cb-small"
                formControlName="isHardStop"
                (nzCheckedChange)="
                  onSectionHardStopChange(sectionGroup, $event)
                "
                >Hard Stop</span
              >
              <button
                type="button"
                class="help-btn"
                [class.has-text]="sectionGroup.value.helpText"
                nz-tooltip
                nzTooltipTitle="Add / Edit Help Text"
                (click)="toggleHelp('s-' + i)">
                <svg-icon
                  src="/assets/icon/icon-help.svg"
                  class="icon s-18"
                  aria-label="Help"></svg-icon>
              </button>
              <button
                type="button"
                class="hardstop-gate-badge"
                *ngIf="
                  sectionGroup.value.sectionName === 'Questionnaires' &&
                  sectionGroup.value.isHardStop
                "
                nz-tooltip
                nzTooltipTitle="Until the Questionnaire is completed, all other sections stay disabled">
                Complete Questionnaire First
              </button>
              <nz-switch formControlName="isSelected"></nz-switch>
            </div>
          </div>

          <div class="help-row" *ngIf="isHelpOpen('s-' + i)">
            <div class="help-input">
              <textarea
                nz-input
                class="h-input"
                rows="4"
                aria-label="Help text"
                [attr.aria-describedby]="'section-help-text-counter-' + i"
                maxlength="4000"
                formControlName="helpText"
                placeholder="Add guidance shown to the end user for this section"></textarea>
              <p class="support-text" [id]="'section-help-text-counter-' + i">
                {{ sectionGroup.value.helpText.length || 0 }} of 4000 characters
                used
              </p>
            </div>
          </div>

          <div
            class="custom-data-wrapper"
            *ngIf="
              sectionGroup.value.sectionName === 'Challenging Behavior' &&
              isSectionSelected(sectionGroup)
            "
            cdkDropList
            id="challengingBehaviorList"
            [cdkDropListData]="challengingBehaviorQuestions"
            [cdkDropListConnectedTo]="['customQuestionsList']"
            [cdkDropListSortPredicate]="challengingBehaviorSortPredicate"
            (cdkDropListDropped)="dropQuestion($event)">
            <div
              *ngFor="
                let question of challengingBehaviorQuestions;
                let qi = index;
                trackBy: trackByQuestion
              "
              cdkDrag
              [cdkDragData]="question"
              [cdkDragDisabled]="
                isViewMode || isFixedBehaviorQuestion(question)
              ">
              <div
                class="custom-question-item"
                [class.excluded]="!question.isIncluded">
                <span
                  class="drag-handle"
                  *ngIf="!isViewMode && !isFixedBehaviorQuestion(question)"
                  cdkDragHandle
                  aria-label="Drag question">
                  <svg-icon
                    src="/assets/icon/icon-drag.svg"
                    class="icon s-16"
                    aria-label="Drag"></svg-icon>
                </span>
                <span
                  nz-checkbox
                  class="question-label"
                  [nzChecked]="question.isIncluded"
                  [nzDisabled]="
                    isViewMode ||
                    question.source === 'custom' ||
                    isFixedBehaviorQuestion(question)
                  "
                  (nzCheckedChange)="toggleQuestionIncluded(question, $event)"
                  >{{ question.label }}
                  <svg-icon
                    *ngIf="isEnvironmentQuestion(question)"
                    src="/assets/icon/icon-info.svg"
                    class="icon s-16 no-question-info"
                    nz-tooltip
                    nzTooltipTitle="If Environment is not selected, Reactive, Replacement Behavior, and Proactive strategies will not be included in system suggestions. Please configure an Environment before proceeding."
                    role="img"
                    tabindex="0"
                    aria-label="If Environment is not selected, Reactive, Replacement Behavior, and Proactive strategies will not be included in system suggestions. Please configure an Environment before proceeding."></svg-icon
                ></span>
                <div class="q-flags">
                  <span
                    nz-checkbox
                    class="cb-small"
                    [nzChecked]="question.isRequired"
                    [nzDisabled]="
                      isViewMode || isFixedBehaviorQuestion(question)
                    "
                    (nzCheckedChange)="
                      onQuestionRequiredChange(question, $event)
                    "
                    >Required</span
                  >
                  <span
                    nz-checkbox
                    class="cb-small"
                    [nzChecked]="question.isHardStop"
                    [nzDisabled]="
                      isViewMode || isFixedBehaviorQuestion(question)
                    "
                    (nzCheckedChange)="
                      onQuestionHardStopChange(question, $event)
                    "
                    >Hard Stop</span
                  >
                  <button
                    type="button"
                    class="help-btn"
                    [class.has-text]="question.helpText"
                    nz-tooltip
                    nzTooltipTitle="Add / edit help text"
                    (click)="
                      toggleHelp('behavior-' + trackByQuestion(qi, question))
                    ">
                    <svg-icon
                      src="/assets/icon/icon-help.svg"
                      class="icon s-16"
                      aria-label="Help"></svg-icon>
                  </button>
                </div>
              </div>
              <div
                class="help-row"
                *ngIf="isHelpOpen('behavior-' + trackByQuestion(qi, question))">
                <div class="help-input">
                  <textarea
                    nz-input
                    class="h-input"
                    rows="4"
                    aria-label="Help text"
                    [value]="question.helpText"
                    [readOnly]="isViewMode"
                    (input)="
                      updateQuestionHelpText(
                        question,
                        $any($event.target).value
                      )
                    "
                    placeholder="Add guidance shown to the end user for this question"></textarea>
                  <p class="support-text">
                    {{ question.helpText.length || 0 }} of 4000 characters used
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div
            class="custom-data-wrapper"
            *ngIf="
              sectionGroup.value.sectionName === 'Custom Questions' &&
              isSectionSelected(sectionGroup)
            "
            cdkDropList
            id="customQuestionsList"
            [cdkDropListData]="customQuestions"
            [cdkDropListConnectedTo]="['challengingBehaviorList']"
            [cdkDropListEnterPredicate]="customQuestionsEnterPredicate"
            (cdkDropListDropped)="dropQuestion($event)">
            <div class="no-question" *ngIf="customQuestions.length === 0">
              No custom questions available for this school.
            </div>
            <div
              *ngFor="
                let question of customQuestions;
                let qi = index;
                trackBy: trackByQuestion
              "
              cdkDrag
              [cdkDragData]="question"
              [cdkDragDisabled]="isViewMode">
              <div
                class="custom-question-item"
                [class.excluded]="!question.isIncluded">
                <span
                  class="drag-handle"
                  *ngIf="!isViewMode"
                  cdkDragHandle
                  aria-label="Drag question">
                  <svg-icon
                    src="/assets/icon/icon-drag.svg"
                    class="icon s-16"
                    aria-label="Drag"></svg-icon>
                </span>
                <span
                  nz-checkbox
                  class="question-label"
                  [nzChecked]="question.isIncluded"
                  [nzDisabled]="isViewMode"
                  (nzCheckedChange)="toggleQuestionIncluded(question, $event)"
                  >{{ question.label }}</span
                >
                <div class="q-flags">
                  <span
                    nz-checkbox
                    class="cb-small"
                    [nzChecked]="question.isRequired"
                    [nzDisabled]="isViewMode"
                    (nzCheckedChange)="
                      onQuestionRequiredChange(question, $event)
                    "
                    >Required</span
                  >
                  <span
                    nz-checkbox
                    class="cb-small"
                    [nzChecked]="question.isHardStop"
                    [nzDisabled]="isViewMode"
                    (nzCheckedChange)="
                      onQuestionHardStopChange(question, $event)
                    "
                    >Hard Stop</span
                  >
                  <button
                    type="button"
                    class="help-btn"
                    [class.has-text]="question.helpText"
                    nz-tooltip
                    nzTooltipTitle="Add / edit help text"
                    (click)="
                      toggleHelp('custom-' + trackByQuestion(qi, question))
                    ">
                    <svg-icon
                      src="/assets/icon/icon-help.svg"
                      class="icon s-16"
                      aria-label="Help"></svg-icon>
                  </button>
                </div>
              </div>
              <div
                class="help-row"
                *ngIf="isHelpOpen('custom-' + trackByQuestion(qi, question))">
                <div class="help-input">
                  <textarea
                    nz-input
                    class="h-input"
                    rows="4"
                    aria-label="Help text"
                    [value]="question.helpText"
                    [readOnly]="isViewMode"
                    (input)="
                      updateQuestionHelpText(
                        question,
                        $any($event.target).value
                      )
                    "
                    placeholder="Add guidance shown to the end user for this question"></textarea>
                  <p class="support-text">
                    {{ question.helpText.length || 0 }} of 4000 characters used
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div
            class="custom-data-wrapper"
            *ngIf="
              sectionGroup.value.sectionName === 'Questionnaires' &&
              isSectionSelected(sectionGroup)
            "
            cdkDropList
            id="questionnaireItemsList"
            [cdkDropListData]="questionnaireItems"
            (cdkDropListDropped)="dropQuestion($event)">
            <div class="no-question" *ngIf="questionnaireItems.length === 0">
              No plans available for this school.
            </div>
            <div
              *ngFor="
                let question of questionnaireItems;
                let qi = index;
                trackBy: trackByQuestion
              "
              cdkDrag
              [cdkDragData]="question"
              [cdkDragDisabled]="isViewMode">
              <div
                class="custom-question-item"
                [class.excluded]="!question.isIncluded">
                <span
                  class="drag-handle"
                  *ngIf="!isViewMode"
                  cdkDragHandle
                  aria-label="Drag question">
                  <svg-icon
                    src="/assets/icon/icon-drag.svg"
                    class="icon s-16"
                    aria-label="Drag"></svg-icon>
                </span>
                <span
                  nz-checkbox
                  class="question-label"
                  [nzChecked]="question.isIncluded"
                  [nzDisabled]="isViewMode"
                  (nzCheckedChange)="toggleQuestionIncluded(question, $event)"
                  >{{ question.label }}
                  <svg-icon
                    *ngIf="question.hasNoActiveQuestions"
                    src="/assets/icon/icon-info.svg"
                    class="icon s-16 no-question-info"
                    nz-tooltip
                    nzTooltipTitle="No Active Questions: This Plan Will Not Appear in Case Attachments"
                    aria-label="No Active Questions Warning"></svg-icon
                ></span>

                <div class="q-flags">
                  <span
                    nz-checkbox
                    class="cb-small"
                    [nzChecked]="question.isRequired"
                    [nzDisabled]="isViewMode"
                    (nzCheckedChange)="
                      onQuestionRequiredChange(question, $event)
                    "
                    >Required</span
                  >
                  <span
                    nz-checkbox
                    class="cb-small"
                    [nzChecked]="question.isHardStop"
                    [nzDisabled]="isViewMode"
                    (nzCheckedChange)="
                      onQuestionHardStopChange(question, $event)
                    "
                    >Hard Stop</span
                  >
                  <button
                    type="button"
                    class="help-btn"
                    [class.has-text]="question.helpText"
                    nz-tooltip
                    nzTooltipTitle="Add / edit help text"
                    (click)="
                      toggleHelp(
                        'questionnaire-' + trackByQuestion(qi, question)
                      )
                    ">
                    <svg-icon
                      src="/assets/icon/icon-help.svg"
                      class="icon s-16"
                      aria-label="Help"></svg-icon>
                  </button>
                </div>
              </div>
              <div
                class="help-row"
                *ngIf="
                  isHelpOpen('questionnaire-' + trackByQuestion(qi, question))
                ">
                <div class="help-input">
                  <textarea
                    nz-input
                    class="h-input"
                    rows="4"
                    aria-label="Help text"
                    [value]="question.helpText"
                    [readOnly]="isViewMode"
                    (input)="
                      updateQuestionHelpText(
                        question,
                        $any($event.target).value
                      )
                    "
                    placeholder="Add guidance shown to the end user for this question"></textarea>
                  <p class="support-text">
                    {{ question.helpText.length || 0 }} of 4000 characters used
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="error-message" *ngIf="!isViewMode && !isAnySectionSelected">
        Select at least one template section
      </div>
    </div>

    <div class="drawer-footer">
      <div class="footer-left">
        <button nz-button type="button" nzType="default" (click)="onCancel()">
          {{ isViewMode ? 'Close' : 'Cancel' }}
        </button>
      </div>
      <div class="footer-right" *ngIf="!isViewMode">
        <button
          nz-button
          type="button"
          nzType="primary"
          (click)="submit()"
          [disabled]="!isFormValid || (loading$ | async)">
          Save to District Records
        </button>
      </div>
    </div>
  </form>
</nz-spin>
